import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FieldOption } from "@/config/forms/general";
import { cn } from "@/lib/utils";
import * as React from "react";

interface HandleValue<T> {
  defaultVal: T;
  getSelect: (value: T) => string;
  getInput: (value: T) => string;
  onValueChange: (val: T | undefined) => void;
  constructValue: (v: {
    select: string | undefined;
    input: string;
  }) => T | undefined;
}

type EmptyType = Record<PropertyKey, never>;

export interface BaseInputSelectProps
  extends React.InputHTMLAttributes<HTMLDivElement> {
  options: FieldOption[];
  select?: Omit<React.ComponentProps<typeof Select>, "onValueChange">;
  input?: Omit<React.ComponentProps<typeof Input>, "defaultValue">;
  selectPlaceholder?: string;
}

export type InputSelectProps<T> = BaseInputSelectProps &
  (EmptyType | HandleValue<T>);

const InputSelect = <T,>({
  select,
  input,
  options,
  selectPlaceholder,
  defaultVal,
  className,
  constructValue,
  getInput,
  getSelect,
  onValueChange,
  ...props
}: InputSelectProps<T>) => {
  const liveSelectValue = React.useMemo(
    () => (defaultVal ? getSelect(defaultVal) : (select?.defaultValue ?? "")),
    [defaultVal, getSelect, select?.defaultValue],
  );
  const liveInputValue = React.useMemo(
    () => (defaultVal ? getInput(defaultVal) : ""),
    [defaultVal, getInput],
  );

  const doOnValueChange = React.useCallback(
    (select: string, input: string) => {
      if (onValueChange && constructValue) {
        onValueChange(constructValue({ select, input }));
      }
    },
    [onValueChange, constructValue],
  );

  const [innerState, setInnerState] = React.useState({
    input: liveInputValue,
    select: liveSelectValue,
  });

  const prevDefaultValRef = React.useRef(defaultVal);
  const hasUserInteractedRef = React.useRef(false);
  const innerStateRef = React.useRef(innerState);

  // Keep ref in sync with state
  React.useEffect(() => {
    innerStateRef.current = innerState;
  }, [innerState]);

  // Update innerState when defaultVal changes (e.g., when form data loads)
  // Only update if defaultVal actually changed and user hasn't interacted
  React.useEffect(() => {
    const defaultValChanged = prevDefaultValRef.current !== defaultVal;

    if (defaultValChanged) {
      const stateIsEmpty =
        !innerStateRef.current.input && !innerStateRef.current.select;

      // Update if: state is empty (initial load) or user hasn't interacted yet
      if (stateIsEmpty || !hasUserInteractedRef.current) {
        setInnerState({
          input: liveInputValue,
          select: liveSelectValue,
        });
      }

      prevDefaultValRef.current = defaultVal;
    }
  }, [defaultVal, liveInputValue, liveSelectValue]);

  // Track user interaction
  const handleInputChange = React.useCallback(
    (v: React.ChangeEvent<HTMLInputElement>) => {
      hasUserInteractedRef.current = true;
      const newInputValue = v.target.value;
      setInnerState((s) => {
        doOnValueChange(s.select, newInputValue);
        return { ...s, input: newInputValue };
      });
    },
    [doOnValueChange],
  );

  const handleSelectChange = React.useCallback(
    (v: string) => {
      hasUserInteractedRef.current = true;
      setInnerState((s) => {
        if (v !== "") doOnValueChange(v, s.input);
        return { ...s, select: v };
      });
    },
    [doOnValueChange],
  );

  return (
    <div
      className={cn(
        "flex w-full gap-2 border shadow-sm rounded-md bg-white",
        className,
      )}
      {...props}
    >
      <Input
        {...input}
        value={innerState.input}
        className={cn("border-none shadow-none")}
        onChange={handleInputChange}
      />
      <Select
        {...select}
        value={innerState.select}
        onValueChange={handleSelectChange}
      >
        <SelectTrigger className={cn("w-fit border-none shadow-none bg-white")}>
          <SelectValue placeholder={selectPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {options?.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default InputSelect;
